// =============================================================================
// PEDRA — src/components/ResultsPanel.tsx
// Area direita do app, em DUAS COLUNAS, como no CutList Optimizer original:
//
//   [ diagrama + navegador de chapas + miniaturas ] [ coluna de cartoes ]
//
// A coluna da direita empilha cartoes colapsaveis (Estatisticas, Estatisticas
// da chapa, Pecas em falta, Cortes, Por chapa, Sobras, Fita de borda) — todos
// visiveis ao mesmo tempo, sem abas para cacar. Abaixo de 1200px ela desce
// para baixo do diagrama; abaixo de 800px vira uma lista simples.
//
// Tambem cuida dos estados vazio / calculando / cancelado / erro.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactElement, ReactNode } from 'react';
import type {
  EdgeBandType,
  Id,
  OptimizationPhase,
  OptimizationResult,
  ProgressEvent,
  Settings,
  SheetLayout,
} from '../core/types';
import type { OptimizerStatus } from '../state/useOptimizer';
import { formatBigLength, formatDimensions, formatNumber, formatPercent } from '../core/units';
import { formatCount } from '../lib/format';
import { SheetDiagram } from './SheetDiagram';
import {
  EdgeBandsPanel,
  OffcutsPanel,
  SheetStatsPanel,
  SheetsTablePanel,
  StatsPanel,
  hasEdgeBandTotals,
} from './StatsPanel';
import { CutListPanel } from './CutListPanel';
import { UnfitPanel } from './UnfitPanel';
import { PdfDownloadButton } from './PdfDownloadButton';
import type { DiagramLayers } from './PdfDownloadButton';

// -----------------------------------------------------------------------------
// TIPOS
// -----------------------------------------------------------------------------

export interface ResultsPanelProps {
  result: OptimizationResult | null;
  settings: Settings;
  edgeBands: readonly EdgeBandType[];
  status: OptimizerStatus;
  progress: ProgressEvent | null;
  error: string | null;
  /** true quando o projeto mudou depois do calculo que gerou `result`. */
  stale?: boolean;
  onCalculate(): void;
  onCancel(): void;
  onLoadSample(): void;
  /** Nome do projeto, para o titulo e o nome do arquivo PDF. */
  projectName?: string;
  /** Aviso flutuante do app (resultado do "Baixar PDF"). */
  onNotify?(tone: 'success' | 'danger', text: string): void;
}

/** Corte em destaque, sincronizado entre a tabela de cortes e o diagrama. */
interface CutHighlight {
  sheetIndex: number;
  seq: number;
}

const PHASE_LABELS: Readonly<Record<OptimizationPhase, string>> = {
  validate: 'Validando os dados…',
  prepare: 'Preparando as peças…',
  search: 'Otimizando o plano de corte…',
  finalize: 'Finalizando…',
};

const ZOOM_STEPS: readonly number[] = [0.5, 0.75, 1, 1.5, 2, 3, 4];
/** Limite de chapas desenhadas no bloco exclusivo de impressao. */
const MAX_PRINT_SHEETS = 40;

// -----------------------------------------------------------------------------
// CARTOES COLAPSAVEIS DA COLUNA
// -----------------------------------------------------------------------------

type CardId = 'stats' | 'sheet' | 'unfit' | 'cuts' | 'sheets' | 'offcuts' | 'bands';

/** Chave propria: nao mexe nas preferencias gerais de interface. */
const CARDS_KEY = 'pedra.cards.v1';

/** Quais cartoes nascem abertos: os quatro do original; o resto sob demanda. */
const DEFAULT_CARDS: Readonly<Record<CardId, boolean>> = {
  stats: true,
  sheet: true,
  unfit: true,
  cuts: true,
  sheets: false,
  offcuts: false,
  bands: false,
};

type CardState = Record<CardId, boolean>;

function loadCardState(): CardState {
  const base: CardState = { ...DEFAULT_CARDS };
  try {
    if (typeof localStorage === 'undefined') return base;
    const raw = localStorage.getItem(CARDS_KEY);
    if (raw === null) return base;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return base;
    for (const key of Object.keys(base) as CardId[]) {
      const v = (parsed as Record<string, unknown>)[key];
      if (typeof v === 'boolean') base[key] = v;
    }
  } catch {
    // Armazenamento bloqueado ou JSON corrompido: fica no padrao.
  }
  return base;
}

function saveCardState(state: CardState): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(CARDS_KEY, JSON.stringify(state));
  } catch {
    // Sem espaco ou modo restrito: recolher deixa de ser lembrado, so isso.
  }
}

interface CardProps {
  id: CardId;
  title: string;
  open: boolean;
  onToggle(id: CardId): void;
  tone?: 'default' | 'danger';
  badge?: string | null;
  children: ReactNode;
}

function Card(props: CardProps): ReactElement {
  const { id, title, open, onToggle, tone = 'default', badge = null, children } = props;
  const classes = ['res-card'];
  if (tone === 'danger') classes.push('res-card--danger');
  if (!open) classes.push('is-collapsed');
  return (
    <section className={classes.join(' ')} aria-labelledby={`res-cardhead-${id}`}>
      <h3 className="res-card-head">
        <button
          type="button"
          id={`res-cardhead-${id}`}
          className="res-card-toggle"
          aria-expanded={open}
          aria-controls={`res-cardbody-${id}`}
          onClick={() => onToggle(id)}
        >
          <span className="res-card-caret" aria-hidden="true">
            ▸
          </span>
          <span className="res-card-title">{title}</span>
          {badge !== null ? <span className="badge badge--danger res-card-badge">{badge}</span> : null}
        </button>
      </h3>
      <div className="res-card-body" id={`res-cardbody-${id}`} hidden={!open}>
        {children}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// MINIATURA DE CHAPA
// -----------------------------------------------------------------------------

interface ThumbProps {
  sheet: SheetLayout;
  active: boolean;
  onSelect(index: number): void;
}

function SheetThumb(props: ThumbProps): ReactElement {
  const { sheet, active, onSelect } = props;
  const w = Math.max(1, sheet.width);
  const l = Math.max(1, sheet.length);
  return (
    <button
      type="button"
      className={active ? 'res-thumb is-active' : 'res-thumb'}
      onClick={() => onSelect(sheet.index)}
      aria-pressed={active}
      title={`Chapa ${sheet.index + 1} — ${formatPercent(sheet.usedAreaRatio)} de aproveitamento`}
    >
      <svg viewBox={`0 0 ${w} ${l}`} className="res-thumb-svg" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <rect className="res-thumb-bg" x={0} y={0} width={w} height={l} />
        {sheet.placements.map((p) => (
          <rect
            key={`${p.partId}#${p.instance}`}
            x={p.x}
            y={p.y}
            width={p.width}
            height={p.length}
            fill={p.color}
            className="res-thumb-part"
          />
        ))}
      </svg>
      <span className="res-thumb-label">
        {sheet.index + 1}
        {sheet.repeat > 1 ? `×${sheet.repeat}` : ''}
      </span>
    </button>
  );
}

// -----------------------------------------------------------------------------
// ESTADOS AUXILIARES
// -----------------------------------------------------------------------------

function EmptyState(props: { onCalculate(): void; onLoadSample(): void }): ReactElement {
  return (
    <div className="res-empty empty-state">
      <svg className="res-empty-art" viewBox="0 0 160 100" aria-hidden="true">
        <rect x="6" y="10" width="148" height="80" rx="3" className="res-empty-sheet" />
        <rect x="14" y="18" width="58" height="34" className="res-empty-part" />
        <rect x="78" y="18" width="68" height="20" className="res-empty-part" />
        <rect x="14" y="58" width="40" height="24" className="res-empty-part" />
        <rect x="60" y="58" width="42" height="24" className="res-empty-part" />
      </svg>
      <h2 className="res-empty-title">Nenhum plano de corte ainda</h2>
      <p className="res-empty-text">
        Preencha as peças e as chapas na barra lateral e clique em <strong>Calcular</strong> para
        gerar o plano de corte.
      </p>
      <div className="res-empty-actions">
        <button type="button" className="btn btn--primary" onClick={props.onCalculate}>
          Calcular agora
        </button>
        <button type="button" className="btn" onClick={props.onLoadSample}>
          Carregar exemplo
        </button>
      </div>
    </div>
  );
}

function RunningState(props: { progress: ProgressEvent | null; onCancel(): void }): ReactElement {
  const { progress, onCancel } = props;
  const percent = progress ? Math.max(0, Math.min(100, progress.percent)) : 0;
  const best = progress?.best ?? null;
  return (
    <div className="res-running" role="status" aria-live="polite">
      <div className="spinner res-running-spinner" aria-hidden="true" />
      <h2 className="res-running-title">{progress ? PHASE_LABELS[progress.phase] : 'Otimizando…'}</h2>
      <div className="progress res-running-bar">
        <div className="progress__bar" style={{ width: `${percent}%` }} />
      </div>
      <p className="res-running-meta tabular">
        {`${formatNumber(percent, 0)}%`}
        {progress ? ` · ${formatNumber(progress.elapsedMs / 1000, 1)} s` : ''}
        {progress && progress.iterations > 0 ? ` · ${formatCount(progress.iterations)} tentativas` : ''}
      </p>
      {best ? (
        <p className="res-running-best">
          {`Melhor até agora: ${formatCount(best.numSheets)} chapa(s) · ` +
            `${formatPercent(best.usedAreaRatio)} de aproveitamento` +
            (best.numUnfitParts > 0 ? ` · ${formatCount(best.numUnfitParts)} sem lugar` : '')}
        </p>
      ) : null}
      <button type="button" className="btn btn--danger" onClick={onCancel}>
        Cancelar cálculo
      </button>
    </div>
  );
}

function ErrorState(props: { message: string; onRetry(): void }): ReactElement {
  return (
    <div className="res-error" role="alert">
      <h2 className="res-error-title">Não foi possível calcular</h2>
      <p className="res-error-text">{props.message}</p>
      <button type="button" className="btn btn--primary" onClick={props.onRetry}>
        Tentar novamente
      </button>
    </div>
  );
}

/** Cancelar o calculo precisa dar retorno: sem isso a tela parece "nao fez nada". */
function CancelledState(props: { onRetry(): void }): ReactElement {
  return (
    <div className="res-cancelled-state" role="status">
      <svg className="res-cancelled-art" viewBox="0 0 48 48" aria-hidden="true">
        <circle cx="24" cy="24" r="19" className="res-cancelled-ring" />
        <path d="M17 17 L31 31 M31 17 L17 31" className="res-cancelled-x" />
      </svg>
      <h2 className="res-cancelled-title">Cálculo cancelado</h2>
      <p className="res-cancelled-text">
        Você interrompeu a otimização antes do fim, então nenhum plano de corte foi gerado.
      </p>
      <button type="button" className="btn btn--primary" onClick={props.onRetry}>
        Calcular novamente
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// COMPONENTE PRINCIPAL
// -----------------------------------------------------------------------------

export function ResultsPanel(props: ResultsPanelProps): ReactElement {
  const {
    result,
    settings,
    edgeBands,
    status,
    progress,
    error,
    stale = false,
    onCalculate,
    onCancel,
    onLoadSample,
    projectName = '',
    onNotify,
  } = props;

  const [cards, setCards] = useState<CardState>(loadCardState);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [zoomIdx, setZoomIdx] = useState(2);
  const [showLabels, setShowLabels] = useState(true);
  const [showDimensions, setShowDimensions] = useState(true);
  const [showCuts, setShowCuts] = useState(true);
  const [showEdgeBanding, setShowEdgeBanding] = useState(true);
  const [colorFilled, setColorFilled] = useState(true);
  const [hoveredPartId, setHoveredPartId] = useState<Id | null>(null);
  const [cutHighlight, setCutHighlight] = useState<CutHighlight | null>(null);
  const [box, setBox] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const unfitCardRef = useRef<HTMLDivElement | null>(null);

  const sheets: readonly SheetLayout[] = result?.sheets ?? [];
  const unfit = result?.unfit ?? [];
  const warnings = result?.warnings ?? [];

  /** Selo e alerta falam a mesma lingua: PECAS, nunca linhas da tabela. */
  const unfitParts = useMemo(() => unfit.reduce((a, u) => a + u.qty, 0), [unfit]);

  const toggleCard = useCallback((id: CardId): void => {
    setCards((prev) => {
      const next: CardState = { ...prev, [id]: !prev[id] };
      saveCardState(next);
      return next;
    });
  }, []);

  // Ao chegar um resultado novo, volta para a primeira chapa.
  useEffect(() => {
    setSheetIndex(0);
    setCutHighlight(null);
    setHoveredPartId(null);
  }, [result]);

  // Mede a area util do visor para escalar o SVG.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const measure = (): void => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [result]);

  const safeIndex = sheets.length === 0 ? 0 : Math.min(sheetIndex, sheets.length - 1);
  const sheet = sheets[safeIndex] ?? null;

  const goTo = useCallback(
    (i: number) => {
      if (sheets.length === 0) return;
      const n = sheets.length;
      setSheetIndex(((i % n) + n) % n);
    },
    [sheets.length],
  );

  const handleHoverCut = useCallback((idx: number, seq: number | null) => {
    setCutHighlight(seq === null ? null : { sheetIndex: idx, seq });
    if (seq !== null) setSheetIndex(idx);
  }, []);

  const handleViewportKey = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        goTo(safeIndex + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goTo(safeIndex - 1);
      }
    },
    [goTo, safeIndex],
  );

  /** Alerta de pecas em falta: abre o cartao vermelho e rola ate ele. */
  const revealUnfit = useCallback((): void => {
    setCards((prev) => {
      if (prev.unfit) return prev;
      const next: CardState = { ...prev, unfit: true };
      saveCardState(next);
      return next;
    });
    window.requestAnimationFrame(() => {
      unfitCardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, []);

  const zoom = ZOOM_STEPS[zoomIdx] ?? 1;
  const diagramWidth = useMemo(() => {
    if (!sheet) return 320;
    const availW = box.w > 0 ? Math.max(200, box.w - 28) : 720;
    const availH = box.h > 0 ? Math.max(200, box.h - 28) : 520;
    const fit = Math.min(availW, (availH * Math.max(1, sheet.width)) / Math.max(1, sheet.length));
    return Math.max(160, fit * zoom);
  }, [sheet, box.w, box.h, zoom]);

  const bandingOn = settings.edgeBandingEnabled && edgeBands.length > 0;

  /** O PDF sai com as mesmas camadas que estao ligadas na tela. */
  const pdfLayers: DiagramLayers = useMemo(
    () => ({
      showLabels,
      showDimensions,
      showCuts,
      showEdgeBanding: showEdgeBanding && bandingOn,
      colorFilled,
    }),
    [showLabels, showDimensions, showCuts, showEdgeBanding, bandingOn, colorFilled],
  );

  // Bloco exclusivo de impressao: uma chapa por pagina, sem zoom/pan da tela.
  // Memoizado para que o hover no diagrama nao redesenhe tudo.
  const printBlock = useMemo(() => {
    if (!result || result.sheets.length === 0 || result.sheets.length > MAX_PRINT_SHEETS) return null;
    return (
      <div className="res-print" aria-hidden="true">
        {result.sheets.map((s) => (
          <section className="res-sheet" key={s.index}>
            <h3 className="res-sheet-title">
              {`Chapa ${s.index + 1} de ${result.sheets.length} — ${s.label || 'Sem nome'} — ` +
                `${formatDimensions(s.length, s.width, settings.unit, settings.decimalPlaces, settings.dimensionOrder)}` +
                (s.repeat > 1 ? ` — ${s.repeat} unidades iguais` : '')}
            </h3>
            <SheetDiagram
              sheet={s}
              settings={settings}
              showLabels={showLabels}
              showDimensions={showDimensions}
              showCuts={showCuts}
              showEdgeBanding={showEdgeBanding && bandingOn}
              colorFilled={colorFilled}
              edgeBands={edgeBands}
              maxWidth={900}
            />
          </section>
        ))}
      </div>
    );
  }, [
    result,
    settings,
    edgeBands,
    showLabels,
    showDimensions,
    showCuts,
    showEdgeBanding,
    colorFilled,
    bandingOn,
  ]);

  const busy = status === 'running';
  const cancelled = status === 'cancelled';
  const showEmpty = !result && !busy && !cancelled && status !== 'error';
  /** Cancelou e havia um plano anterior: ele continua na tela, mas avisado. */
  const showCancelledNotice = cancelled && result !== null && !busy;
  /** Entradas mudaram depois do calculo: o plano na tela nao vale mais. */
  const showStaleNotice = stale && result !== null && !busy && !cancelled && status !== 'error';
  /** So ha o que mostrar na coluna quando existe um plano calculado. */
  const showColumn = !busy && result !== null && status !== 'error';

  return (
    <section
      className={showStaleNotice ? 'res-root is-stale' : 'res-root'}
      aria-label="Resultado da otimização"
    >
      {result ? (
        <div className="res-topbar no-print">
          <span className="res-topbar-info tabular">
            {`${formatCount(result.stats.numSheets)} ${result.stats.numSheets === 1 ? 'chapa' : 'chapas'} · ` +
              `${formatPercent(result.stats.usedAreaRatio)} de aproveitamento · ` +
              `${formatCount(result.stats.numCuts)} cortes`}
          </span>
          {showStaleNotice ? <span className="res-stale-chip">desatualizado</span> : null}
          {result.sheets.length > 0 ? (
            <PdfDownloadButton
              projectName={projectName}
              result={result}
              settings={settings}
              edgeBands={edgeBands}
              layers={pdfLayers}
              stale={stale}
              disabled={busy}
              onNotify={onNotify}
            />
          ) : null}
        </div>
      ) : null}

      {showStaleNotice ? (
        <div className="res-stale" role="status">
          <span className="res-stale-icon" aria-hidden="true">
            ⚠
          </span>
          <span className="res-stale-text">
            <strong>As entradas mudaram — recalcule.</strong> O plano exibido abaixo é o do cálculo
            anterior e não corresponde mais às peças e chapas atuais.
          </span>
          <button type="button" className="btn btn--sm btn--primary" onClick={onCalculate}>
            Recalcular
          </button>
        </div>
      ) : null}

      {showCancelledNotice ? (
        <div className="res-cancelled" role="status">
          <span className="res-stale-icon" aria-hidden="true">
            ⨯
          </span>
          <span className="res-stale-text">
            <strong>Cálculo cancelado.</strong> O plano exibido abaixo é o do cálculo anterior
            {stale
              ? ' e, como as entradas mudaram, ele não corresponde mais às peças e chapas atuais.'
              : '.'}
          </span>
          <button type="button" className="btn btn--sm btn--primary" onClick={onCalculate}>
            Tentar novamente
          </button>
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div className="res-warnings" role="status">
          {warnings.map((w, i) => (
            <p key={i} className="res-warning">
              {w}
            </p>
          ))}
        </div>
      ) : null}

      {unfitParts > 0 ? (
        <div className="res-alert" role="status">
          <span>{`${formatCount(unfitParts)} peça(s) não couberam nas chapas disponíveis.`}</span>
          <button type="button" className="btn btn--sm btn--ghost" onClick={revealUnfit}>
            Ver detalhes
          </button>
        </div>
      ) : null}

      <div className="res-layout">
        <div className="res-center">
          {busy ? <RunningState progress={progress} onCancel={onCancel} /> : null}

          {!busy && status === 'error' ? (
            <ErrorState message={error ?? 'Erro desconhecido no cálculo.'} onRetry={onCalculate} />
          ) : null}

          {!busy && cancelled && result === null ? <CancelledState onRetry={onCalculate} /> : null}

          {showEmpty ? <EmptyState onCalculate={onCalculate} onLoadSample={onLoadSample} /> : null}

          {showColumn && result ? (
            sheet ? (
              <>
                <div className="res-sheetbar no-print">
                  <div className="res-nav">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => goTo(safeIndex - 1)}
                      disabled={sheets.length < 2}
                      aria-label="Chapa anterior"
                      title="Chapa anterior"
                    >
                      ◀
                    </button>
                    <select
                      className="select res-sheet-select"
                      value={safeIndex}
                      onChange={(e) => goTo(Number(e.target.value))}
                      aria-label="Selecionar chapa"
                    >
                      {sheets.map((s) => (
                        <option key={s.index} value={s.index}>
                          {`Chapa ${s.index + 1} de ${sheets.length}` +
                            (s.repeat > 1 ? ` (${s.repeat}×)` : '') +
                            ` — ${formatPercent(s.usedAreaRatio)}`}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => goTo(safeIndex + 1)}
                      disabled={sheets.length < 2}
                      aria-label="Próxima chapa"
                      title="Próxima chapa"
                    >
                      ▶
                    </button>
                  </div>

                  <div className="res-sheet-meta truncate">
                    {`${sheet.label || 'Chapa sem nome'} · ` +
                      `${formatDimensions(sheet.length, sheet.width, settings.unit, settings.decimalPlaces, settings.dimensionOrder)}` +
                      (sheet.material ? ` · ${sheet.material}` : '') +
                      (sheet.repeat > 1 ? ` · repete ${sheet.repeat}×` : '')}
                  </div>

                  <div className="res-zoom">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
                      disabled={zoomIdx === 0}
                      aria-label="Diminuir zoom"
                      title="Diminuir zoom"
                    >
                      −
                    </button>
                    <span className="res-zoom-value tabular">{`${Math.round(zoom * 100)}%`}</span>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
                      disabled={zoomIdx === ZOOM_STEPS.length - 1}
                      aria-label="Aumentar zoom"
                      title="Aumentar zoom"
                    >
                      +
                    </button>
                    <button type="button" className="btn btn--sm" onClick={() => setZoomIdx(2)}>
                      Ajustar
                    </button>
                  </div>
                </div>

                <div
                  className="res-viewport"
                  ref={viewportRef}
                  tabIndex={0}
                  onKeyDown={handleViewportKey}
                  aria-label="Visor do diagrama. Use as setas para trocar de chapa."
                >
                  <SheetDiagram
                    sheet={sheet}
                    settings={settings}
                    showLabels={showLabels}
                    showDimensions={showDimensions}
                    showCuts={showCuts}
                    showEdgeBanding={showEdgeBanding && bandingOn}
                    colorFilled={colorFilled}
                    edgeBands={edgeBands}
                    maxWidth={diagramWidth}
                    highlightedPartId={hoveredPartId}
                    highlightedCutSeq={
                      cutHighlight && cutHighlight.sheetIndex === sheet.index ? cutHighlight.seq : null
                    }
                    onHoverPart={setHoveredPartId}
                  />
                </div>

                {sheets.length > 1 ? (
                  <div className="res-thumbs no-print" aria-label="Miniaturas das chapas">
                    {sheets.map((s) => (
                      <SheetThumb key={s.index} sheet={s} active={s.index === safeIndex} onSelect={goTo} />
                    ))}
                  </div>
                ) : null}

                <div className="res-toggles no-print">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={showLabels}
                      onChange={(e) => setShowLabels(e.target.checked)}
                    />
                    <span>Rótulos</span>
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={showDimensions}
                      onChange={(e) => setShowDimensions(e.target.checked)}
                    />
                    <span>Cotas</span>
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={showCuts}
                      onChange={(e) => setShowCuts(e.target.checked)}
                    />
                    <span>Linhas de corte</span>
                  </label>
                  {bandingOn ? (
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={showEdgeBanding}
                        onChange={(e) => setShowEdgeBanding(e.target.checked)}
                      />
                      <span>Fitas de borda</span>
                    </label>
                  ) : null}
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={colorFilled}
                      onChange={(e) => setColorFilled(e.target.checked)}
                    />
                    <span>Peças coloridas</span>
                  </label>
                </div>

                <div className="res-strip tabular">
                  <span>
                    <strong>{formatPercent(result.stats.usedAreaRatio)}</strong> aproveitamento
                  </span>
                  <span>
                    <strong>{formatCount(result.stats.numSheets)}</strong>{' '}
                    {result.stats.numSheets === 1 ? 'chapa' : 'chapas'}
                  </span>
                  <span>
                    <strong>{formatCount(result.stats.numCuts)}</strong> cortes
                  </span>
                  <span>
                    <strong>{formatBigLength(result.stats.totalCutLength, settings.unit)}</strong> de
                    corte
                  </span>
                </div>
              </>
            ) : (
              <div className="res-empty empty-state">
                <p>Nenhuma chapa foi usada: verifique as peças e o estoque.</p>
              </div>
            )
          ) : null}
        </div>

        {showColumn && result ? (
          <aside className="res-side no-print" aria-label="Resumo do resultado">
            <Card id="stats" title="Estatísticas" open={cards.stats} onToggle={toggleCard}>
              <StatsPanel result={result} settings={settings} />
            </Card>

            <Card id="sheet" title="Estatísticas da chapa" open={cards.sheet} onToggle={toggleCard}>
              <SheetStatsPanel sheet={sheet} settings={settings} sheetCount={sheets.length} />
            </Card>

            {unfitParts > 0 ? (
              <div ref={unfitCardRef}>
                <Card
                  id="unfit"
                  title="Peças em falta"
                  open={cards.unfit}
                  onToggle={toggleCard}
                  tone="danger"
                  badge={formatCount(unfitParts)}
                >
                  <UnfitPanel unfit={unfit} settings={settings} />
                </Card>
              </div>
            ) : null}

            <Card id="cuts" title="Cortes" open={cards.cuts} onToggle={toggleCard}>
              <CutListPanel
                result={result}
                settings={settings}
                sheetIndex={null}
                selectedIndex={safeIndex}
                onHoverCut={handleHoverCut}
                onSelectSheet={goTo}
              />
            </Card>

            <Card id="sheets" title="Por chapa" open={cards.sheets} onToggle={toggleCard}>
              <SheetsTablePanel
                result={result}
                settings={settings}
                selectedIndex={safeIndex}
                onSelectSheet={goTo}
              />
            </Card>

            <Card id="offcuts" title="Sobras" open={cards.offcuts} onToggle={toggleCard}>
              <OffcutsPanel result={result} settings={settings} />
            </Card>

            {hasEdgeBandTotals(result) ? (
              <Card id="bands" title="Fita de borda" open={cards.bands} onToggle={toggleCard}>
                <EdgeBandsPanel result={result} settings={settings} edgeBands={edgeBands} />
              </Card>
            ) : null}
          </aside>
        ) : null}
      </div>

      {printBlock}
    </section>
  );
}

export default ResultsPanel;
